import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { formValues, formValues2, schema, schema2 } from "./validations";
import { useNavigate, useParams } from "react-router-dom";
import { getSchoolRedirectPath } from "@/routes/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { TabsDataTypes } from "../types";
import {
  AGREEMENT_DOCUMENT,
  AGREEMENT_DOCUMENT_FILE_NAME,
  CONTRACT_YEARS,
  DEAL_AMOUNT,
  DEAL_CLOSED_BY,
  DEAL_CLOSURE_DATE,
  EXPECTED_DUE_DATE,
  EXPECTED_LAUNCH_DATE,
  INCLUDES_GST,
  INSTALLMENT,
  INSTALLMENT_AMOUNT,
  INSTALLMENT_NUMBER,
  INSTALLMENT_YEAR,
  INTEREST_AMOUNT,
  INTEREST_PERCENTAGE,
  LEAD_TYPE,
  MOU_AMOUNT,
  PAYMENT_FROM_ENTITY,
  PDCS_COLLECTED,
  SALES_PERSON_TYPE,
} from "./consts";
import {
  useGetInstallmentsByCommercialIdQuery,
  useGetSchoolCommercialBySchoolIdQuery,
  useCreateSchoolCommercialMutation,
  useCreateSchoolInstallmentsMutation,
  useUpdateSchoolCommercialMutation,
} from "@/services";
import { useUpdateInstallmentMutation } from "@/services/school/commercial/installment/useUpdateInstallmentMutation copy";
import moment from "moment";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { LOGIN_ROLES } from "@/lib/utils";
import { useProfilesByRoleQuery } from "@/services/employeeManagement/useProfilesByRoleQuery";
import { toast } from "sonner";

const useInstallmentSchedule = ({ onNextClick }: TabsDataTypes) => {
  const navigate = useNavigate();
  const { id, action } = useParams();
  const attachmentRef = useRef<any>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [openUpdatePdc, setOpenUpdatePdc] = useState<boolean>(false);
  const [openAddInstallment, setOpenAddInstallment] = useState<boolean>(false);
  const isEdit = action === "edit" && !!id;

  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      // Deal Info
      [DEAL_CLOSURE_DATE]: "",
      [DEAL_AMOUNT]: "",
      [INCLUDES_GST]: "",
      [PDCS_COLLECTED]: "",
      [SALES_PERSON_TYPE]: "",
      [DEAL_CLOSED_BY]: "",
      [LEAD_TYPE]: "",
      [CONTRACT_YEARS]: "",
      [PAYMENT_FROM_ENTITY]: "",
      [EXPECTED_LAUNCH_DATE]: "",
      [AGREEMENT_DOCUMENT]: null,
      [AGREEMENT_DOCUMENT_FILE_NAME]: "",
    },
  });

  const form2 = useForm<formValues2>({
    resolver: zodResolver(schema2),
    defaultValues: {
      // Installment Schedule
      [INSTALLMENT]: Array.from({ length: 2 }, () => ({
        _id: "",
        [INSTALLMENT_YEAR]: "",
        [INSTALLMENT_NUMBER]: "",
        [EXPECTED_DUE_DATE]: "",
        [INSTALLMENT_AMOUNT]: "",
        [MOU_AMOUNT]: "",
        [INTEREST_PERCENTAGE]: "",
      })),
    },
  });

  const requiredFields = getRequiredFields(schema); 

  const toYMD = (value: any) => {
    if (!value) return "";
    const m = moment(value);
    return m.isValid() ? m.format("YYYY-MM-DD") : "";
  };

  // Helper to check if user has a given role
  const roles = loggedInUserData?.role || [];
  const hasRole = (role: string) =>
    Array.isArray(roles) && roles.includes(role);

  const isSalesOpsOrSuperAdmin =
    hasRole(LOGIN_ROLES.SALES_OPS) || hasRole(LOGIN_ROLES.SUPER_ADMIN);

  // 🔹 Automatically calculate Installment Amount as user types (only for SALES_OPS and SUPER_ADMIN)
  useEffect(() => {
    // only run auto-calculation for allowed roles
    if (!isSalesOpsOrSuperAdmin) return;

    // Subscribe to field changes
    const subscription = form2.watch((value, { name }) => {
      if (typeof name !== "string") return;

      // only respond to mouAmount or interestPercentage changes
      if (!name.includes(MOU_AMOUNT) && !name.includes(INTEREST_PERCENTAGE))
        return;

      const match = name.match(/installment\.(\d+)\./);
      if (!match) return;
      const index = Number(match[1]);

      const mouAmount = parseFloat(
        value?.installment?.[index]?.mouAmount ?? ""
      );
      const interestPercent = parseFloat(
        value?.installment?.[index]?.interestPercentage ?? ""
      );

      if (!isNaN(mouAmount) && !isNaN(interestPercent)) {
        const interestAmount = (mouAmount * interestPercent) / 100;
        form2.setValue(
          `installment.${index}.installmentAmount`,
          interestAmount.toFixed(2),
          {
            shouldValidate: true,
            shouldDirty: true,
          }
        );
      } else {
        form2.setValue(`installment.${index}.installmentAmount`, "", {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [form2, isSalesOpsOrSuperAdmin]);

  // Edit mode: fetch commercial and installments, prefill form
  const { data: commercialResp } = useGetSchoolCommercialBySchoolIdQuery(
    String(id) || "",
    { enabled: isEdit } as any
  );

  const commercial = useMemo(() => {
    const commercialData = (commercialResp as any)?.data?.[0]?.records;
    console.log({ commercialData });
    
    // If data is an array, pick the first commercial
    if (Array.isArray(commercialData) && commercialData.length > 0) {
      return commercialData[0];
    }
    // If data is an object, use it directly
    return commercialData || {};
  }, [commercialResp]);

  const commercialId =
    commercial?.commercialId || commercial?.id || commercial?._id || "";

  const { data: installmentsResp, isPending: isPending } =
    useGetInstallmentsByCommercialIdQuery(commercialId, {
      enabled: isEdit && !!commercialId,
    } as any);

  // Fetch salesops employees for Deal Closed By dropdown
  const { data: salesOpsEmployeesData } = useProfilesByRoleQuery(
    LOGIN_ROLES.SALES_OPS
  );

  const dealClosedByOptions =
    salesOpsEmployeesData?.data?.[0]?.records?.map((profile: any) => ({
      label: profile.name,
      value: profile.profile_id,
    })) || [];

  useEffect(() => {
    if (!isEdit) return;

    const dealDefaults: Partial<formValues> = {
      [DEAL_CLOSURE_DATE]: commercial?.dealCloserDate ?? "",
      [DEAL_AMOUNT]: String(commercial?.dealAmount ?? ""),
      [INCLUDES_GST]: commercial?.isGstIncluded ?? "",
      [PDCS_COLLECTED]: commercial?.isPdcCollected ? "Yes" : "No",
      [SALES_PERSON_TYPE]: commercial?.salesPersonType ?? "",
      [DEAL_CLOSED_BY]: commercial?.dealClosedBy ?? "",
      [LEAD_TYPE]: commercial?.leadType ?? "",
      [CONTRACT_YEARS]: commercial?.contractYear
        ? String(commercial?.contractYear)
        : "",
      [PAYMENT_FROM_ENTITY]: commercial?.paymentFromEntity ?? "",
      [EXPECTED_LAUNCH_DATE]: commercial?.expectedLaunchDate ?? "",
      [AGREEMENT_DOCUMENT]: commercial?.aggrementDocument || null,
      [AGREEMENT_DOCUMENT_FILE_NAME]: commercial
        ? commercial?.agreementDocumentFileName
        : "",
    } as any;
    const installmentsData = (installmentsResp as any)?.data;
    // Handle both array and object responses
    const rawInstallments = Array.isArray(installmentsData)
      ? installmentsData
      : installmentsData
      ? [installmentsData]
      : [];

    const mappedInstallments = rawInstallments.length
      ? rawInstallments.map((it) => ({
          _id: it?.installmentId || "",
          [INSTALLMENT_YEAR]: String(it?.year ?? ""),
          [INSTALLMENT_NUMBER]: String(it?.installmentNumber ?? "0"),
          [EXPECTED_DUE_DATE]: it?.expectedDueDate ?? "",
          [INSTALLMENT_AMOUNT]: String(it?.installmentAmount ?? "0"),
          [MOU_AMOUNT]: String(it?.mouAmount ?? "0"),
          [INTEREST_PERCENTAGE]: String(it?.installmentPercentage ?? "0"),
        }))
      : undefined;

    form.reset({
      ...(form.getValues() as any),
      ...(dealDefaults as any),
    });
    form2.reset({
      ...(form2.getValues() as any),
      ...(mappedInstallments
        ? { [INSTALLMENT]: mappedInstallments as any }
        : {}),
    });
    setIsLoading(false);
  }, [isEdit, commercialResp, installmentsResp]);

  const { mutateAsync: createCommercial } = useCreateSchoolCommercialMutation();
  const { mutateAsync: updateInstallment } = useUpdateInstallmentMutation();
  const { mutateAsync: createInstallments } = useCreateSchoolInstallmentsMutation();

  const { mutateAsync: updateCommercial } = useUpdateSchoolCommercialMutation();

  const onSubmit = async (data: formValues) => {
    const cleaned = setEmptyFieldsToNull(removeEmptyFields(data)) as any;
    // Prepare Commercial payload (create or update)
    const commercialPayload = {
      schoolId: String(id) || "",
      dealCloserDate: toYMD(cleaned[DEAL_CLOSURE_DATE]),
      dealAmount: Number(cleaned[DEAL_AMOUNT] || 0),
      isGstIncluded: cleaned[INCLUDES_GST] || "",
      isPdcCollected: cleaned[PDCS_COLLECTED] ?? "No",
      salesPersonType: cleaned[SALES_PERSON_TYPE] || "",
      dealClosedBy: cleaned[DEAL_CLOSED_BY] || "",
      leadType: cleaned[LEAD_TYPE] || "",
      contractYear: cleaned[CONTRACT_YEARS] || "",
      paymentFromEntity: cleaned[PAYMENT_FROM_ENTITY] || "",
      expectedLaunchDate: toYMD(cleaned[EXPECTED_LAUNCH_DATE]),
      aggrementDocument: cleaned[AGREEMENT_DOCUMENT] || null,
    };

    // 1) Create or Update Commercial
    let commercialRes: any;
    if (isEdit && commercialId) {
      commercialRes = await updateCommercial({
        commercialId,
        ...commercialPayload,
      });
    } else {
      commercialRes = await createCommercial(commercialPayload);
    }
    const savedCommercial = commercialRes?.data || {};
    const savedCommercialId =
      savedCommercial?.commercialId ||
      savedCommercial?.id ||
      savedCommercial?._id ||
      commercialId;

    // 2) Create Installments
    const installmentsData = (cleaned[INSTALLMENT] || []).map((it: any) => ({
      schoolId: String(id) || "",
      commercialId: savedCommercialId,
      year: Number(it[INSTALLMENT_YEAR]),
      installmentNumber: Number(it[INSTALLMENT_NUMBER] || 0),
      expectedDueDate: toYMD(it[EXPECTED_DUE_DATE]),
      installmentAmount: Number(it[INSTALLMENT_AMOUNT] || 0),
      mouAmount: Number(it[MOU_AMOUNT] || 0),
      installmentPercentage: Number(it[INTEREST_PERCENTAGE] || 0),
      isPdcCheckAvailable:
        String(cleaned[PDCS_COLLECTED] || "no").toLowerCase() === "yes",
    }));

    // 2) Create Installments
    if (isEdit) {
      const existingInstallmentsData = (installmentsResp as any)?.data || [];
      const existingInstallments = Array.isArray(existingInstallmentsData)
        ? existingInstallmentsData
        : [existingInstallmentsData];

      const updatePromises = (cleaned[INSTALLMENT] || []).map(
        async (it: any) => {
          // find the matching installment by number/year
          const match = existingInstallments.find(
            (ex: any) =>
              Number(ex.year) === Number(it[INSTALLMENT_YEAR]) &&
              Number(ex.installmentNumber) === Number(it[INSTALLMENT_NUMBER])
          );

          if (match?.installmentId) {
            await updateInstallment({
              schoolId: String(id) || "",
              installmentId: match.installmentId, // ✅ use id instead of index
              year: Number(it[INSTALLMENT_YEAR] || 0),
              installmentNumber: Number(it[INSTALLMENT_NUMBER] || 0),
              expectedDueDate: toYMD(it[EXPECTED_DUE_DATE]),
              installmentAmount: Number(it[INSTALLMENT_AMOUNT] || 0),
              mouAmount: Number(it[MOU_AMOUNT] || 0),
              installmentPercentage: Number(it[INTEREST_PERCENTAGE] || 0),
              isPdcCheckAvailable:
                String(cleaned[PDCS_COLLECTED] || "no").toLowerCase() === "yes",
            });
          }
        }
      );

      await Promise.all(updatePromises);
    }

    onNextClick();
  };

  const onSubmit2 = async (data: formValues2) => {
    const cleaned = setEmptyFieldsToNull(removeEmptyFields(data)) as any;

    const savedCommercialId = commercialId;

    // 2) Create Installments
    const installmentsData = (cleaned[INSTALLMENT] || []).map((it: any) => ({
      schoolId: String(id) || "",
      commercialId: savedCommercialId,
      year: Number(it[INSTALLMENT_YEAR] || 0),
      installmentNumber: Number(it[INSTALLMENT_NUMBER] || 0),
      expectedDueDate: toYMD(it[EXPECTED_DUE_DATE]),
      installmentAmount: Number(it[INSTALLMENT_AMOUNT] || 0),
      mouAmount: Number(it[MOU_AMOUNT] || 0),
      installmentPercentage: Number(it[INTEREST_PERCENTAGE] || 0),
      isPdcCheckAvailable:
        String(cleaned[PDCS_COLLECTED] || "no").toLowerCase() === "yes",
    }));

    if (savedCommercialId) {
      // 2) Create Installments
      if (isEdit) {
        const existingInstallmentsData = (installmentsResp as any)?.data || [];
        const existingInstallments = Array.isArray(existingInstallmentsData)
          ? existingInstallmentsData
          : [existingInstallmentsData];
  
          const updatePromises: Promise<any>[] = [];
          const createData: any[] = [];
  
        (cleaned[INSTALLMENT] || [])?.map(
          (it: any) => {
            console.log("🚀 ~ onSubmit2 ~ it:", it)
            // find the matching installment by number/year
            const match = existingInstallments.find(
              (ex: any) =>
                Number(ex.year) === Number(it[INSTALLMENT_YEAR]) &&
                Number(ex.installmentNumber) === Number(it[INSTALLMENT_NUMBER])
            );
  
            if (match?.installmentId) {
              updatePromises.push(updateInstallment({
                schoolId: String(id) || "",
                installmentId: match.installmentId, // ✅ use id instead of index
                year: Number(it[INSTALLMENT_YEAR] || 0),
                installmentNumber: Number(it[INSTALLMENT_NUMBER] || 0),
                expectedDueDate: toYMD(it[EXPECTED_DUE_DATE]),
                installmentAmount: Number(it[INSTALLMENT_AMOUNT] || 0),
                mouAmount: Number(it[MOU_AMOUNT] || 0),
                installmentPercentage: Number(it[INTEREST_PERCENTAGE] || 0),
                isPdcCheckAvailable:
                  String(cleaned[PDCS_COLLECTED] || "no").toLowerCase() === "yes",
              }))
            } else {
              const isPdcAvailable =
                String(cleaned[PDCS_COLLECTED] || "no").toLowerCase() === "yes";
              const payload: any = {
                schoolId: String(id) || "",
                commercialId: savedCommercialId,
                year: Number(it[INSTALLMENT_YEAR] || 0),
                installmentNumber: Number(it[INSTALLMENT_NUMBER] || 0),
                expectedDueDate: toYMD(it[EXPECTED_DUE_DATE]),
                installmentAmount: Number(it[INSTALLMENT_AMOUNT] || 0),
                mouAmount: Number(it[MOU_AMOUNT] || 0),
                installmentPercentage: Number(it[INTEREST_PERCENTAGE] || 0),
                isPdcCheckAvailable: isPdcAvailable,
              };
  
              // Include PDC data if available and PDCs are collected
              if (
                isPdcAvailable &&
                it.pdcData &&
                Array.isArray(it.pdcData) &&
                it.pdcData.length > 0
              ) {
                payload.pdcData = it.pdcData.map((pdc: any) => ({
                  year: Number(pdc.year || 0),
                  pdcNumber: Number(pdc.pdcNumber || 0),
                  pdcDate: toYMD(pdc.pdcDate),
                  pdcAmount: Number(pdc.pdcAmount || 0),
                  pdcRemarks: pdc.pdcRemarks || null,
                }));
              }
  
              createData.push(payload)
            }
          }
        );
  
        updatePromises.push(createInstallments({ installmentsData: createData }))
        await Promise.all(updatePromises);
        onNextClick();
      }
    } else {
      toast.error("Commercial Info not found", {
        className: "!bg-red-500 !text-white",
      });
    }
  };

  const onCancel = () => {
    const userRole = loggedInUserData?.role || [];
    navigate(getSchoolRedirectPath(userRole));
  };

  const handleScrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth", // smooth scroll
      });
    }
  };

  const onAddNewAcademicInfo = () => {
    setOpenAddInstallment(true);
  };

  const onRemoveAcademicInfo = (index: number) => {
    const tasks = [...form2.getValues(INSTALLMENT)];
    tasks.splice(index, 1);
    form2.setValue(INSTALLMENT, tasks);
    setTimeout(() => handleScrollToBottom(), 200);
  };

  const onCancelUpdatePdc = () => {
    setOpenUpdatePdc(false);
  };

  const onUpdatePdcClick = () => {
    setOpenUpdatePdc(true);
  };

  const onCancelInstallment = () => {
    setOpenAddInstallment(false);
  };

  return {
    form,
    form2,
    requiredFields,
    onSubmit,
    onSubmit2,
    onCancel,
    attachmentRef,
    onAddNewAcademicInfo,
    onRemoveAcademicInfo,
    scrollRef,
    openUpdatePdc,
    openAddInstallment,
    onCancelInstallment,
    onCancelUpdatePdc,
    onUpdatePdcClick,
    isLoading: isLoading,
    hasRole,
    roles,
    dealClosedByOptions,
  };
};

export default useInstallmentSchedule;
